import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { distributionPartnerKeys, distributionPartnersApi } from '@client/api/waterlily';
import type { API } from '@/api/types';

export type DistributionContactWithPartner = API.DistributionPartnerContact & { partnerKey: string };

interface UseDistributionPartnerConnectionOptions {
  onConnected?: () => void;
}

export function useDistributionPartnerConnection(
  options: UseDistributionPartnerConnectionOptions = {},
) {
  const queryClient = useQueryClient();
  // Multiple firms can be selected; contacts aggregate across all of them.
  const [selectedPartnerKeys, setSelectedPartnerKeys] = useState<string[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  // The advisor-level opt-out. Defaults to checked (auto-share on) until the saved record loads.
  const [shareConsent, setShareConsent] = useState(true);

  const partnersQuery = useQuery({
    queryKey: distributionPartnerKeys.partners(),
    queryFn: () => distributionPartnersApi.listPartners(),
    staleTime: 5 * 60 * 1000,
  });

  const sortedPartnerKeys = useMemo(() => [...selectedPartnerKeys].sort(), [selectedPartnerKeys]);

  // Fetch contacts for every selected firm in parallel, tagging each with its partnerKey so the
  // connect step can group them back by firm.
  const contactsQuery = useQuery({
    queryKey: [...distributionPartnerKeys.all, 'contacts-multi', sortedPartnerKeys] as const,
    queryFn: async () => {
      const groups = await Promise.all(
        sortedPartnerKeys.map(async key => {
          const contacts = await distributionPartnersApi.getContacts(key);
          return contacts.map(contact => ({ ...contact, partnerKey: key }));
        }),
      );

      return groups.flat() as DistributionContactWithPartner[];
    },
    enabled: sortedPartnerKeys.length > 0,
    staleTime: 60 * 1000,
  });

  const consentQuery = useQuery({
    queryKey: distributionPartnerKeys.sharingConsent(),
    queryFn: () => distributionPartnersApi.getSharingConsent(),
    staleTime: 60 * 1000,
  });

  const legacyClientsQuery = useQuery({
    queryKey: distributionPartnerKeys.hasLegacyClients(),
    queryFn: () => distributionPartnersApi.getHasLegacyClients(),
    staleTime: 60 * 1000,
  });

  // Reflect the saved opt-out once it loads. A null record (never set) keeps the default-on state.
  useEffect(() => {
    const saved = consentQuery.data?.hasConsented;

    if (typeof saved === 'boolean') {
      setShareConsent(saved);
    }
  }, [consentQuery.data]);

  // Restore the partners the advisor is already connected to once the list loads, so a page reload
  // shows their saved selection. Runs once; the advisor controls the selection afterwards.
  const didInitConnectedPartnersRef = useRef(false);
  useEffect(() => {
    if (didInitConnectedPartnersRef.current || !partnersQuery.data) return;

    const connectedKeys = partnersQuery.data
      .filter(partner => partner.isConnected)
      .map(partner => partner.partnerKey);

    didInitConnectedPartnersRef.current = true;

    if (connectedKeys.length > 0) {
      setSelectedPartnerKeys(prev => Array.from(new Set([...prev, ...connectedKeys])));
    }
  }, [partnersQuery.data]);

  // Pre-check contacts the advisor is already connected with, so the list reflects who they've
  // already connected. Merged into the selection (never removes the advisor's own picks).
  useEffect(() => {
    if (!contactsQuery.data) return;

    const connectedIds = contactsQuery.data
      .filter(contact => contact.isConnected)
      .map(contact => contact.advisorId);

    if (connectedIds.length === 0) return;

    setSelectedContactIds(prev => Array.from(new Set([...prev, ...connectedIds])));
  }, [contactsQuery.data]);

  const contacts = contactsQuery.data ?? [];

  const partnerKeyByContactId = useMemo(() => {
    const map = new Map<string, string>();
    contacts.forEach(contact => map.set(contact.advisorId, contact.partnerKey));
    return map;
  }, [contacts]);

  const togglePartner = useCallback((partnerKey: string) => {
    setSelectedPartnerKeys(prev =>
      prev.includes(partnerKey) ? prev.filter(key => key !== partnerKey) : [...prev, partnerKey],
    );
  }, []);

  const toggleContact = useCallback((advisorId: string, checked: boolean) => {
    setSelectedContactIds(prev => {
      const set = new Set(prev);

      if (checked) {
        set.add(advisorId);
      } else {
        set.delete(advisorId);
      }

      return Array.from(set);
    });
  }, []);

  const toggleContacts = useCallback((advisorIds: string[], checked: boolean) => {
    setSelectedContactIds(prev => {
      const set = new Set(prev);

      advisorIds.forEach(id => (checked ? set.add(id) : set.delete(id)));

      return Array.from(set);
    });
  }, []);

  const connectMutation = useMutation({
    // Persist the opt-out choice from Modal 1 first (so the server's connect-time backfill sees the
    // current consent), then connect each firm's selected contacts.
    mutationFn: async (autoShare: boolean) => {
      await distributionPartnersApi.setSharingConsent(autoShare);

      const byPartner = new Map<string, string[]>();
      selectedContactIds.forEach(id => {
        const key = partnerKeyByContactId.get(id);

        if (!key) return;

        byPartner.set(key, [...(byPartner.get(key) ?? []), id]);
      });

      for (const [partnerKey, contactIds] of byPartner) {
        await distributionPartnersApi.connect(partnerKey, contactIds);
      }
    },
    onSuccess: () => {
      toast.success('Partner contacts connected');
      // Revalidate so the partners' and contacts' isConnected flags reflect the just-saved
      // connections — the selection persists and shows as connected.
      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.all });
      options.onConnected?.();
    },
    onError: (error: unknown) => {
      toast.error('Failed to connect contacts', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: ({
      partnerKey,
      contactAdvisorId,
      scope,
    }: {
      partnerKey: string;
      contactAdvisorId: string;
      scope: API.DistributionPartnerDisconnectScope;
    }) => distributionPartnersApi.disconnect(partnerKey, contactAdvisorId, scope),
    onSuccess: (_data, { contactAdvisorId, scope }) => {
      // Drop the contact from the in-session selection and optimistically clear its isConnected
      // flag, so the card unchecks and the trash button disappears immediately — before the
      // contacts query refetches.
      setSelectedContactIds(prev => prev.filter(id => id !== contactAdvisorId));
      queryClient.setQueriesData<DistributionContactWithPartner[]>(
        { queryKey: [...distributionPartnerKeys.all, 'contacts-multi'] },
        prev =>
          prev?.map(contact =>
            contact.advisorId === contactAdvisorId ? { ...contact, isConnected: false } : contact,
          ),
      );
      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.all });

      toast.success(
        scope === 'all'
          ? 'Disconnected and stopped sharing all clients'
          : 'Stopped sharing future clients',
      );
    },
    onError: (error: unknown) => {
      toast.error('Failed to update sharing', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const requestOtherMutation = useMutation({
    mutationFn: ({ partnerKey, name, email }: { partnerKey: string; name: string; email: string }) =>
      distributionPartnersApi.requestOther(partnerKey, name.trim(), email.trim()),
    onSuccess: () => {
      toast.success('Invite sent', {
        description: "We've emailed them an invitation to join Waterlily as your partner.",
      });
    },
    onError: (error: unknown) => {
      toast.error('Failed to send request', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  // Batch legacy-client share (Modal 2 / settings): record the attestation and share all of the
  // advisor's pre-new-consent clients with their connected partner contacts.
  const legacyShareMutation = useMutation({
    mutationFn: (hasConsented: boolean) => distributionPartnersApi.shareLegacyClients(hasConsented),
    onSuccess: (_data, hasConsented) => {
      queryClient.invalidateQueries({ queryKey: distributionPartnerKeys.hasLegacyClients() });

      if (hasConsented) {
        toast.success('Your earlier clients were shared with your partner contacts');
      }
    },
    onError: (error: unknown) => {
      toast.error('Failed to share your earlier clients', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const connect = useCallback(
    (autoShare: boolean) => {
      if (selectedContactIds.length === 0) return;

      connectMutation.mutate(autoShare);
    },
    [selectedContactIds, connectMutation],
  );

  const requestOther = useCallback(
    (partnerKey: string, name: string, email: string) => {
      if (!partnerKey || !name.trim() || !email.trim()) return;

      requestOtherMutation.mutate({ partnerKey, name, email });
    },
    [requestOtherMutation],
  );

  const disconnect = useCallback(
    (
      partnerKey: string,
      contactAdvisorId: string,
      scope: API.DistributionPartnerDisconnectScope,
      onDone?: () => void,
    ) => {
      if (!partnerKey || !contactAdvisorId) return;

      disconnectMutation.mutate(
        { partnerKey, contactAdvisorId, scope },
        { onSuccess: () => onDone?.() },
      );
    },
    [disconnectMutation],
  );

  const confirmLegacyShare = useCallback(
    (onDone?: () => void) => {
      legacyShareMutation.mutate(true, { onSuccess: () => onDone?.() });
    },
    [legacyShareMutation],
  );

  return {
    partners: partnersQuery.data ?? [],
    isLoadingPartners: partnersQuery.isLoading,
    contacts,
    isLoadingContacts: contactsQuery.isLoading,
    selectedPartnerKeys,
    selectedContactIds,
    togglePartner,
    toggleContact,
    toggleContacts,
    connect,
    isConnecting: connectMutation.isPending,
    requestOther,
    isRequestingOther: requestOtherMutation.isPending,
    disconnect,
    isDisconnecting: disconnectMutation.isPending,
    // The saved opt-out, used to pre-select the checkbox in the connect confirmation dialog.
    shareConsent,
    hasLegacyClients: legacyClientsQuery.data?.hasLegacyClients ?? false,
    hasConnections: legacyClientsQuery.data?.hasConnections ?? false,
    hasLegacyShareConsent: legacyClientsQuery.data?.hasLegacyShareConsent ?? false,
    confirmLegacyShare,
    isSharingLegacy: legacyShareMutation.isPending,
  };
}
